import type { Provider } from "../../assessment.ts";
import type { RiskScore } from "../../catalog.ts";
import type { DirectoryGroup } from "../../directory.ts";

export const Usages = ["unused", "used", "unknown"] as const;

export type GrantCondition = {
  title?: string;
  description?: string;
  expression: string;
};

/** Uniquely identifies a single grant */
export type GrantEntry = {
  condition: GrantCondition | undefined;
  createdAt?: number;
  isAncestorGrant: boolean;
  lastUsedAt?: number;
  parent: string;
  privilegeSet: string;
  principal: string;
  principalType: IdentityType;
  resources: string[];
  id?: string;
};

export type IdentityType =
  | "agent"
  | "aws-iam-role"
  | "aws-permission-set-role"
  | "domain"
  | "entra-app-registration"
  | "federated"
  | "group"
  | "logged-in"
  | "public"
  | "service-account"
  | "service-principal"
  | "unknown"
  | "user";

export type MfaStatus = "disabled" | "enabled" | "unknown";

export type TrustedPrincipals = {
  trustPolicy: string;
  isConditional: boolean;
};

export type Identity = {
  access?: DirectoryGroup["access"];
  /** Department that manages this identity */
  department?: string;
  disabled?: boolean;
  /** True if this identity is managed outside the current scope */
  external: boolean;
  isProviderManaged: boolean;
  label: string;
  /** Key of this identity's manager identity; only applies to users */
  manager?: string;
  mfa?: MfaStatus;
  /** This principal's parent resource
   *
   * Applies to service identities (GCP service accounts, AWS IAM roles).
   */
  parent?: string;
  type: IdentityType;
  /** The identity provider for this principal
   *
   * May be undefined if unknown.
   */
  provider: Provider | undefined;
  trustedPrincipals?: TrustedPrincipals;
  trustedAccountPrincipals?: string[];
};

export type CredentialType = "federated" | "key" | "password" | "short-lived";

type Credential = {
  type: CredentialType;
  provider: Provider;
  createdTime?: number;
  expirationTime?: number;
  status: "disabled" | "enabled" | undefined;
  lastAuthnTime?: number;
  // Specifies the number of days of authentication lookback we have for this credential.
  maxAuthnLookbackDays?: number;
};

type CredentialConsumerBase = {
  isProviderManaged: boolean;
  label: string;
  lastUsedTime: number;
  identityType: IdentityType;
  provider: Provider;
};

export type CredentialIpConsumer = CredentialConsumerBase & {
  type: "ip";
  organization?: string;
  userAgents?: string[];
};

export type CredentialPrincipalConsumer = CredentialConsumerBase & {
  type: "principal";
};
export type CredentialConsumer =
  | CredentialIpConsumer
  | CredentialPrincipalConsumer;

export type Resource = {
  /** The resource's (short) identifier */
  id: string;
  /** The full resource locator */
  locator: string;
  /** The resource's service undefined if this is a project/account node*/
  service: string | undefined;
  /** undefined if this is a service node */
  type: string | undefined;
  /** optional icon URL for frontend rendering of this resource */
  iconUrl?: string;
};

export type PrivilegeUsage = {
  privilege: string;
  service: string;
  usage: Usage;
};

export type Grant = {
  condition: GrantCondition | string | undefined;
  /** True iff the principal is managed in a different parent than this grant */
  cross: boolean;
  disabled?: boolean;
  isAncestorGrant: boolean;
  isProviderManaged: boolean;
  /** The resource (e.g. GCP project) in which this grant is provisioned */
  parent: string;
  privilegeSet: string;
  principal: string;
  principalType: IdentityType;
  /** The service provider in which the grant is provisioned */
  provider: Provider;
  /** Grant resource patterns
   *
   * In AWS, this can be any arn pattern.
   * In GCP, this is always a length 1 explicit grant name.
   * In Azure AD, this can be multiple resources.
   */
  resources: string[];
};

export type Usage = (typeof Usages)[number];

/** grant - Lateral movement via direct permissions
 *  resource - Lateral movement via a service identity
 */
export type LateralType = "grant" | "resource";
export type Lateral = {
  type: LateralType;
  /** The identity to which movement is enabled */
  identity: string;
  /** The abused resource(s) that allows movement; in the case of direct movement this is the policy
   *  that yields the necessary priviliges
   */
  resources: string[];
  /** The resource's service */
  service: string;
  /** Constraints that limit the movement */
  constraints: string[];
};

export type PermissionSet = {
  createdDate: number;
  label: string;
  sessionDuration: string;
};

export type AssessmentNodes = {
  condition: GrantCondition | undefined;
  consumer: CredentialConsumer;
  credential: Credential;
  grant: Grant;
  identity: Identity;
  lateral: Lateral;
  privilege: object;
  permissionSet: PermissionSet;
  privilegeSet: { provider: Provider };
  resource: Resource;
  risk: { name: string; score: RiskScore };
  usage: { type: Usage; count?: number };
};
